import React, { useState, useRef } from 'react';
import {
  EmailListTableViewProps,
  EmailSearchResult,
  ThirdPartyToLocalIdRequest,
  ThirdPartyToLocalIdResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';
import Heading from './Heading';
import EmailComponent from './EmailComponent';
import EmailListView, { EmailListItem } from '../../../common/EmailListView';
import { useEmailList } from '../../../../providers/EmailListProvider';
import { useComments } from '../../../../contexts/CommentsContext';
import { Comments } from '../Comments';
import { AIReportingWrapper } from '../AIReportingWrapper';
import api from 'api';

import './email-list-table-view-component.scss';
import '../../../common/email-list-view.scss';

// Component props interface that extends EmailListTableViewProps with threadId
interface EmailListTableViewComponentProps extends EmailListTableViewProps {
  threadId?: string;
  componentId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

// Interface for managing email view state
interface EmailViewState {
  mode: 'list' | 'loading' | 'email';
  selectedEmailId?: string;
  localEmailId?: string;
  error?: string;
}

/**
 * EmailListTableView Component
 *
 * Displays an ordered list of emails with a heading and table structure.
 * Each email has two rows: first with pill/address/date, second with subject/content.
 */
const EmailListTableView: React.FC<EmailListTableViewComponentProps> = ({
  emailListId,
  title,
  threadId,
  componentId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getEmailList } = useEmailList();
  const { comments } = useComments();
  const [viewState, setViewState] = useState<EmailViewState>({ mode: 'list' });
  const emailRefs = useRef<{
    [key: string]: React.RefObject<HTMLDivElement | null>;
  }>({});

  // Helper function to get or create ref for an email
  const getEmailRef = (
    emailId: string,
  ): React.RefObject<HTMLDivElement | null> => {
    if (!emailRefs.current[emailId]) {
      emailRefs.current[emailId] = React.createRef<HTMLDivElement>();
    }
    return emailRefs.current[emailId];
  };

  // Get email list data from provider
  const emailListState = emailListId
    ? getEmailList(emailListId)
    : {
        emails: [],
        query: '',
        loading: false,
        error: 'No email list ID provided',
        lastUpdated: 0,
      };

  const { emails, query, loading, error } = emailListState;

  // Get component-specific comments filtered by email targeting metadata
  const getCommentsForEmail = (emailId: string) => {
    if (!componentId || !comments.componentComments[componentId]) {
      return [];
    }
    return comments.componentComments[componentId].filter(
      (comment) => comment.targetingMetadata?.emailId === emailId,
    );
  };

  // No need for useEffect - the provider handles fetching and updates automatically

  const getPillText = (email: EmailSearchResult) => {
    // For search results, we'll determine type based on labels
    if (email.labels?.includes('DRAFT')) return 'Draft';
    if (email.labels?.includes('SENT')) return 'Sent';
    if (email.labels?.includes('UNREAD')) return 'Unread';
    return '';
  };

  const getPillVariant = (
    email: EmailSearchResult,
  ):
    | 'default'
    | 'green'
    | 'red'
    | 'blue'
    | 'yellow'
    | 'light-blue'
    | 'beige' => {
    if (email.labels?.includes('DRAFT')) return 'default';
    if (email.labels?.includes('SENT')) return 'green';
    if (email.labels?.includes('UNREAD')) return 'blue';
    return 'default';
  };

  const getAddressText = (email: EmailSearchResult) => {
    if (email.labels?.includes('DRAFT')) {
      return `${email.to || 'Unknown'}`;
    }
    if (email.labels?.includes('SENT')) {
      return `${email.to || 'Unknown'}`;
    }
    return `${email.from || 'Unknown'}`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No date';
    try {
      const emailDate = new Date(dateString);

      // For today's emails, show relative time
      if (isToday(emailDate)) {
        return formatDistanceToNow(emailDate, { addSuffix: true });
      }

      // For yesterday's emails
      if (isYesterday(emailDate)) {
        return `Yesterday ${format(emailDate, 'HH:mm')}`;
      }

      // For this week's emails
      if (isThisWeek(emailDate)) {
        return format(emailDate, 'EEE HH:mm'); // e.g., "Mon 14:30"
      }

      // For older emails, show formatted date/time: "09:01:33, Thu, 3 Jul"
      return format(emailDate, 'HH:mm:ss, EEE, d MMM');
    } catch {
      return dateString;
    }
  };

  const getEmailSnippet = (email: EmailSearchResult) => {
    // Use the snippet from the search result
    return email.snippet || 'No preview available';
  };

  // Check if all emails have the same pill variant to determine if we should show pills
  const shouldShowPills = () => {
    if (emails.length <= 1) return false;
    const firstPillVariant = getPillVariant(emails[0]);
    return (
      !emails.every((email) => getPillVariant(email) === firstPillVariant) ||
      firstPillVariant === 'blue'
    );
  };

  // Handle clicking on an email to view details
  const handleEmailClick = async (email: EmailSearchResult) => {
    try {
      setViewState({ mode: 'loading', selectedEmailId: email.id });

      const token = await getAccessTokenSilently();
      const requestBody: ThirdPartyToLocalIdRequest = {
        emailListId,
        provider: 'GOOGLE',
        id: email.id,
      };

      const response = await api.put<ThirdPartyToLocalIdResponse>(
        '/agent-email-storage/third-party-to-local-id',
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setViewState({
        mode: 'email',
        selectedEmailId: email.id,
        localEmailId: response.data.localEmailId,
      });
    } catch (err) {
      console.error('Error converting email ID:', err);
      setViewState({
        mode: 'list',
        error: 'Failed to load email details',
      });
    }
  };

  // Handle returning to the email list
  const handleBackToList = () => {
    setViewState({ mode: 'list' });
  };

  // Render content based on current view mode
  const renderContent = () => {
    if (loading) {
      return <div className='email-list-loading'>Loading emails...</div>;
    }

    if (error) {
      return (
        <div className='email-list-error'>Error loading emails: {error}</div>
      );
    }

    // Show individual email view
    if (viewState.mode === 'email' && viewState.localEmailId) {
      return (
        <div className='email-component-wrapper'>
          <div className='email-detail-header'>
            <button
              className='back-to-list-button'
              onClick={handleBackToList}
              type='button'
            >
              ← Back to List
            </button>
          </div>
          <div className='lower-wrapper'>
            <EmailComponent
              emailId={viewState.localEmailId}
              threadId={threadId || ''}
              updateThreadViewVisualDescription={
                updateThreadViewVisualDescription
              }
            />
          </div>
        </div>
      );
    }

    // Show loading state when converting email ID
    if (viewState.mode === 'loading') {
      return (
        <div className='email-component-wrapper'>
          <div className='email-detail-header'>
            <button
              className='back-to-list-button'
              onClick={handleBackToList}
              type='button'
            >
              ← Back to List
            </button>
          </div>
          <div className='lower-wrapper'>
            <div className='loading-spinner-container'>
              <div className='loading-spinner'></div>
            </div>
          </div>
        </div>
      );
    }

    // Show error if there was an issue with email conversion
    if (viewState.error) {
      return <div className='email-list-error'>Error: {viewState.error}</div>;
    }

    // Transform emails to EmailListItem format
    const emailListItems: EmailListItem[] = emails.map((email) => ({
      id: email.id,
      pill: {
        text: getPillText(email),
        variant: getPillVariant(email),
      },
      address: getAddressText(email),
      date: formatDate(email.date),
      subject: email.subject || '(No Subject)',
      snippet: getEmailSnippet(email),
    }));

    // Show email list (default view)
    return (
      <EmailListView
        items={emailListItems}
        showPills={shouldShowPills()}
        onItemClick={(item) => {
          const email = emails.find((e) => e.id === item.id);
          if (email) handleEmailClick(email);
        }}
        renderItemActions={(item) => {
          const emailRef = getEmailRef(item.id);
          const emailComments = getCommentsForEmail(item.id);

          return (
            <>
              <AIReportingWrapper
                breakdown={{
                  name: 'Email',
                  emailId: item.id,
                  emailSubject: item.subject,
                  emailDate: item.date,
                }}
              >
                <div ref={emailRef} style={{ position: 'relative' }} />
              </AIReportingWrapper>
              {componentId && emailComments.length > 0 && (
                <Comments
                  comments={emailComments}
                  baseElementRef={emailRef}
                  targetType='component'
                  targetId={componentId}
                />
              )}
            </>
          );
        }}
      />
    );
  };

  return (
    <AIReportingWrapper
      breakdown={{
        name: 'Email List View',
        emailListId: emailListId,
        showingEmail:
          `Currently showing emaill ${viewState.selectedEmailId}` ||
          'No Email selected',
      }}
    >
      <div className='email-list-table-view'>
        <Heading level='5' value={title} icon='email' />
        {renderContent()}
      </div>
    </AIReportingWrapper>
  );
};

export default EmailListTableView;
